import Alpine from "alpinejs"

Alpine.data("filterStore", function (config = {}) {
    return {
        filters: {},
        activeFilter: null,
        filtersList: [],
        search: "",
        state: "default",
        initialLimit: config.initialLimit || 3,
        visibleCount: 0,
        totalCount: 0,
        vaccinationUrls: config.vaccinationUrls || {},
        currentVisibleItems: 0,
        showingAll: false,

        init() {
            this.filtersList = Array.from(this.$el.querySelectorAll(".c-dropdown"))

            this.$el.addEventListener("dropdown:add-item", (event) => this.handleAddItem(event))
            this.$el.addEventListener("dropdown:remove-item", (event) => this.handleRemoveItem(event))
            this.$el.addEventListener("dropdown:clear-items", (event) => this.handleClearItems(event))
            this.$watch("search", () => this.updateState())

            this.updateState()

            try {
                const urlsData = this.$el.dataset.vaccinationUrls
                if (urlsData) {
                    const urlObjects = JSON.parse(urlsData)
                    // Create a mapping of identifier to URL
                    this.vaccinationUrls = {}
                    urlObjects.forEach((item) => {
                        if (item && item.identifier && item.url) {
                            this.vaccinationUrls[item.identifier] = item.url
                        }
                    })
                }
            } catch (e) {
                console.error("Failed to parse vaccination URLs", e)
                this.vaccinationUrls = {}
            }
        },

        handleAddItem(event) {
            const { id, identifier, title } = event.detail

            this.filters[id] = []
            if (!this.filters[id].some((item) => item.identifier === identifier)) {
                this.filters[id].push({ identifier, title })
                this.activeFilter = id

                this.filtersList.forEach((filter) => {
                    filter.dispatchEvent(
                        new CustomEvent("dropdown:disable-filter", {
                            bubbles: true,
                            detail: { enabledId: id },
                        }),
                    )
                })
            }

            this.showingAll = false
            this.visibleCount = this.initialLimit
            this.updateState()
            this.updateTotalCount()
        },

        handleRemoveItem(event) {
            const { id, identifier } = event.detail

            if (this.filters[id]) {
                this.filters[id] = this.filters[id].filter((item) => item.identifier !== identifier)
                if (this.filters[id].length === 0) {
                    this.activeFilter = null
                }

                this.filtersList.forEach((filter) => {
                    filter.dispatchEvent(
                        new CustomEvent("dropdown:disable-filter", {
                            bubbles: true,
                            detail: { enabledId: this.activeFilter },
                        }),
                    )
                })
            }

            this.showingAll = false
            this.visibleCount = this.initialLimit
            this.updateState()
            this.updateTotalCount()
        },

        handleClearItems(event) {
            const { id } = event.detail
            this.filters[id] = []
            this.activeFilter = this.activeFilter === id ? null : this.activeFilter

            this.filtersList.forEach((filter) => {
                filter.dispatchEvent(
                    new CustomEvent("dropdown:disable-filter", {
                        bubbles: true,
                        detail: { enabledId: null },
                    }),
                )
            })

            this.showingAll = false
            this.visibleCount = this.initialLimit
            this.updateState()
            this.updateTotalCount()
        },

        updateTotalCount() {
            if (!this.activeFilter || !this.filters[this.activeFilter]) {
                this.totalCount = 0
                return
            }

            const selectedFilter = this.filters[this.activeFilter][0]
            if (!selectedFilter) {
                this.totalCount = 0
                return
            }

            const filteredContainer = this.$el.querySelector(
                ".c-facts-check__items:not(.c-facts-check__items--default)",
            )
            if (!filteredContainer) {
                this.totalCount = 0
                return
            }

            const allItems = Array.from(filteredContainer.querySelectorAll(".c-facts-check__item"))
            const matchingItems = allItems.filter((item) => {
                const filterTagsStr = item.dataset.filterTags
                if (!filterTagsStr) return false

                try {
                    const filterTags = JSON.parse(filterTagsStr)
                    return filterTags.includes(selectedFilter.identifier)
                } catch (e) {
                    return false
                }
            })

            this.totalCount = matchingItems.length
        },

        getTotalCount() {
            return this.totalCount
        },

        matchesCriteria(item) {
            const searchTerm = this.search.toLowerCase().trim()
            const title = item.dataset.title?.toLowerCase() || ""
            const copy = item.dataset.copy?.toLowerCase() || ""
            const filterTags = item.dataset.filterTags ? JSON.parse(item.dataset.filterTags) : []

            const matchesSearch = !searchTerm || title.includes(searchTerm) || copy.includes(searchTerm)
            const matchesFilter =
                !this.activeFilter ||
                filterTags.some((tag) => this.filters[this.activeFilter]?.some((filter) => filter.identifier === tag))

            return matchesSearch && matchesFilter
        },
        // Add method to get the URL for a given identifier
        getNodeUrl(identifier) {
            if (!identifier) return "#"

            // Parse the vaccination URLs if needed
            if (!this.vaccinationUrls) {
                try {
                    const urlsData = this.$el.dataset.vaccinationUrls
                    if (urlsData) {
                        const urlObjects = JSON.parse(urlsData)
                        // Create a mapping of identifier to URL
                        this.vaccinationUrls = {}
                        urlObjects.forEach((item) => {
                            if (item && item.identifier && item.url) {
                                this.vaccinationUrls[item.identifier] = item.url
                            }
                        })
                    }
                } catch (e) {
                    this.vaccinationUrls = {}
                }
            }
            return this.vaccinationUrls[identifier] || "#"
        },

        getVisibleCount() {
            return this.currentVisibleItems
        },

        isVisible(item) {
            if (!this.matchesCriteria(item)) return false
            if (!this.isFiltered()) return true

            const container = item.closest(".c-facts-check__items")
            if (!container) return false

            const allItems = Array.from(container.querySelectorAll(".c-facts-check__item"))
            const matchingItems = allItems.filter((i) => this.matchesCriteria(i))
            const position = matchingItems.indexOf(item)

            return position < this.visibleCount
        },

        updateState() {
            const filteredContainer = this.$el.querySelector(
                ".c-facts-check__items:not(.c-facts-check__items--default)",
            )

            if (filteredContainer) {
                const allItems = Array.from(filteredContainer.querySelectorAll(".c-facts-check__item"))
                const matchingItems = allItems.filter((item) => this.matchesCriteria(item))

                if (!this.visibleCount) {
                    this.visibleCount = this.initialLimit
                }

                // Update visible items count based on current state
                if (this.showingAll) {
                    this.currentVisibleItems = matchingItems.length
                } else {
                    this.currentVisibleItems = Math.min(matchingItems.length, this.visibleCount)
                }

                // Update state based on matching items
                if (matchingItems.length === 0) {
                    this.state = "empty"
                } else if (this.activeFilter || this.search.trim()) {
                    this.state = "filtered"
                } else {
                    this.state = "default"
                }
            }

            this.updateTotalCount()
        },

        getActiveFilter() {
            if (!this.activeFilter || !this.filters[this.activeFilter]) {
                return {
                    title: "",
                    identifier: null,
                }
            }

            const activeFilter = this.filters[this.activeFilter][0]
            return {
                title: activeFilter?.title || "",
                identifier: activeFilter?.identifier || null,
            }
        },

        showAll() {
            this.showingAll = true
            this.visibleCount = Infinity
            this.updateState()
        },

        isDefault() {
            return this.state === "default"
        },

        isFiltered() {
            return this.state === "filtered"
        },

        isEmpty() {
            return this.state === "empty"
        },
    }
})
